
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { FileText, Upload, Download, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCrmUsers } from '@/hooks/crm/useCrmUsers';
import { CRMUserFormData } from '@/types/crm';

interface CSVUploadIntegrationProps {
  onSaved: () => Promise<void>;
}

interface PreviewUser {
  name: string;
  email: string;
  phone: string;
  isValid: boolean;
  error?: string;
}

const CSVUploadIntegration: React.FC<CSVUploadIntegrationProps> = ({ onSaved }) => {
  const { toast } = useToast();
  const { addUser } = useCrmUsers();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewUser[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseCSV(selectedFile);
    }
  };

  const parseCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(header => header.trim().toLowerCase());
      
      // Check if required columns exist
      const nameIndex = headers.indexOf('name');
      const emailIndex = headers.indexOf('email');
      const phoneIndex = headers.indexOf('phone');
      
      if (nameIndex === -1 || emailIndex === -1 || phoneIndex === -1) {
        toast({
          title: "Invalid CSV format",
          description: "CSV must contain 'name', 'email', and 'phone' columns.",
          variant: "destructive",
        });
        return;
      }
      
      const parsedData: PreviewUser[] = [];
      
      // Start from index 1 to skip headers
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue; // Skip empty lines
        
        const values = lines[i].split(',').map(value => value.trim());
        
        const name = values[nameIndex];
        const email = values[emailIndex];
        const phone = values[phoneIndex];
        
        // Validate data
        let isValid = true;
        let error = '';
        
        if (!name) {
          isValid = false;
          error += 'Name is required. ';
        }
        
        if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
          isValid = false;
          error += 'Valid email is required. ';
        }
        
        if (!phone) {
          isValid = false;
          error += 'Phone is required.';
        }
        
        parsedData.push({
          name,
          email,
          phone,
          isValid,
          error: error.trim() || undefined
        });
      }
      
      setPreviewData(parsedData);
      setShowPreview(true);
    };
    
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setIsUploading(true);
    
    try {
      // Filter valid entries
      const validEntries = previewData.filter(user => user.isValid);
      
      // Add each valid user to the CRM system
      for (const user of validEntries) {
        const userData: CRMUserFormData = {
          name: user.name,
          email: user.email,
          phone: user.phone,
          tags: ['csv-import']
        };
        
        await addUser(userData);
      }
      
      await onSaved();
      
      toast({
        title: "Upload successful",
        description: `${validEntries.length} users have been successfully imported.`,
      });
      
      // Reset form
      setFile(null);
      setPreviewData([]);
      setShowPreview(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading users:', error);
      toast({
        title: "Upload failed",
        description: "Failed to import users. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = 'name,email,phone\nJohn Doe,john@example.com,555-123-4567\nJane Smith,jane@example.com,555-987-6543';
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_users.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <FileText className="mr-2 h-6 w-6" />
          CSV Upload
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div>
            <Label htmlFor="csv-file" className="mb-2 block">Upload User CSV</Label>
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
              <div className="flex flex-col items-center">
                <Upload className="h-10 w-10 text-muted-foreground mb-4" />
                <p className="text-sm mb-2">Drag and drop your CSV file or</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="relative"
                  >
                    Select CSV File
                    <input
                      ref={fileInputRef}
                      id="csv-file"
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="sr-only"
                    />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={downloadSampleCSV}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Sample
                  </Button>
                </div>
                {file && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Selected file: {file.name}
                  </p>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Your CSV file must include columns for name, email, and phone number.
            </p>
          </div>
          
          {showPreview && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Preview</h3>
                <div className="text-sm text-muted-foreground">
                  Valid entries: {previewData.filter(user => user.isValid).length}/{previewData.length}
                </div>
              </div>
              
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead className="w-24">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.slice(0, 5).map((user, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{index + 1}</TableCell>
                        <TableCell>{user.name || <span className="text-red-500">Missing</span>}</TableCell>
                        <TableCell>{user.email || <span className="text-red-500">Missing</span>}</TableCell>
                        <TableCell>{user.phone || <span className="text-red-500">Missing</span>}</TableCell>
                        <TableCell>
                          {user.isValid ? (
                            <span className="flex items-center text-green-600">
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Valid
                            </span>
                          ) : (
                            <div className="group relative">
                              <span className="flex items-center text-red-600 cursor-help">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                Error
                              </span>
                              <div className="hidden group-hover:block absolute left-0 bottom-full mb-2 p-2 bg-black text-white text-xs rounded shadow-lg w-48 z-10">
                                {user.error}
                              </div>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {previewData.length > 5 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          +{previewData.length - 5} more entries
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              
              <Button 
                onClick={handleUpload} 
                disabled={isUploading || previewData.filter(user => user.isValid).length === 0}
                className="w-full"
              >
                {isUploading ? "Importing..." : "Import Users"}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col items-start border-t bg-muted/50 px-6 py-4">
        <h4 className="text-sm font-medium mb-2">CSV Format Requirements</h4>
        <ul className="space-y-1 text-sm">
          <li className="flex text-muted-foreground">
            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
            <span>Must include headers for name, email, and phone</span>
          </li>
          <li className="flex text-muted-foreground">
            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
            <span>Each user must have a valid email address</span>
          </li>
          <li className="flex text-muted-foreground">
            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
            <span>Maximum 1,000 users per upload (daily invitation limit)</span>
          </li>
        </ul>
      </CardFooter>
    </Card>
  );
};

export default CSVUploadIntegration;
